import { CombinedGraphQLErrors } from "@apollo/client/errors";
import type { IntlShape } from "react-intl";
import { logDiagnostic } from "./diagnostics";
import { CODES } from "@/lib/diagnostics";

export function logApplicationFailure(
  action: string,
  options: {
    code?: string;
    level?: "warn" | "error";
    correlationId?: string;
  } = {},
): void {
  logDiagnostic({
    level: options.level ?? "error",
    code: options.code ?? CODES.application.OPERATION_FAILURE,
    category: "application",
    correlationId: options.correlationId,
    details: { action },
  });
}

// Maps any caught error to a user-safe, localized message (see @adr SPARK-0010-no-raw-server-errors-in-ui@1).
// Raw server messages (graphql-java internals, schema paths, persistence
// details) must never reach the UI: they expose backend logic and are not
// localized. The technical error goes to the console for diagnosis; genesis's
// errorId (GraphQL extensions) is surfaced as a support code when present.
//
// Validation issues from Validate*Result payloads are NOT routed through
// here - those messages are product copy meant for users.
// genesis classifies every GraphQL error it returns
// (`extensions.classification`: VALIDATION, PERSISTENCE, FORBIDDEN, …). Almost
// all of them describe a backend condition the user can do nothing about, so
// they share the generic "try again" copy.
//
// FORBIDDEN is different in kind, not in degree: retrying can never succeed,
// because nothing about the request was wrong — the caller simply lacks a role.
// Telling that user "something went wrong, please try again" sends them into a
// loop against a wall. Naming the class of problem is still not naming the
// server's message: the copy below is ours, the server text stays unrendered
// (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
const CLASSIFIED_COPY: Record<string, { plain: MessageSpec; withCode: MessageSpec }> = {
  FORBIDDEN: {
    plain: {
      id: "common.forbidden",
      defaultMessage: "You do not have the required permissions for this action.",
    },
    withCode: {
      id: "common.forbiddenWithCode",
      defaultMessage: "You do not have the required permissions for this action. (Code {code})",
    },
  },
};

const GENERIC_COPY = {
  plain: {
    id: "common.serverError",
    defaultMessage: "Something went wrong. Please try again.",
  },
  withCode: {
    id: "common.serverErrorWithCode",
    defaultMessage: "Something went wrong. Please try again. (Code {code})",
  },
} satisfies { plain: MessageSpec; withCode: MessageSpec };

interface MessageSpec {
  id: string;
  defaultMessage: string;
}

export function userErrorMessage(intl: IntlShape, error: unknown): string {
  // Application-level diagnostic for a user-visible caught failure. The Apollo
  // error link remains the transport source of truth (graphql/network); this
  // records the distinct application perspective with a stable code and no raw
  // error content. Dev keeps the console for local diagnosis (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
  logApplicationFailure("user-visible-error");
  if (import.meta.env.DEV) console.error("[user-error]", error);
  const { errorId, classification } = extractErrorFacts(error);
  const copy = classification ? (CLASSIFIED_COPY[classification] ?? GENERIC_COPY) : GENERIC_COPY;
  return errorId
    ? intl.formatMessage(copy.withCode, { code: errorId })
    : intl.formatMessage(copy.plain);
}

/**
 * genesis's classification for this error, when it sent one. Exported so a
 * screen that knows *which* permission its action needs can say so, instead of
 * repeating the generic sentence above. Callers must not render it.
 */
export function errorClassification(error: unknown): string | null {
  return extractErrorFacts(error).classification;
}

function extractErrorFacts(error: unknown): { errorId: string | null; classification: string | null } {
  // Apollo v4: `ApolloError` was removed; the wrapper for server-returned
  // GraphQL errors is now `CombinedGraphQLErrors`. Its `.errors` field holds
  // the GraphQLFormattedError[] (was `.graphQLErrors` in v3).
  if (!CombinedGraphQLErrors.is(error)) return { errorId: null, classification: null };
  // Both facts are read off the SAME error: a response can carry several, and
  // pairing one error's code with another's classification would describe a
  // failure that never happened.
  for (const g of error.errors) {
    const errorId = g.extensions?.["errorId"];
    const classification = g.extensions?.["classification"];
    const hasId = typeof errorId === "string";
    const hasClassification = typeof classification === "string";
    if (hasId || hasClassification) {
      return {
        errorId: hasId ? errorId : null,
        classification: hasClassification ? classification : null,
      };
    }
  }
  return { errorId: null, classification: null };
}
